import { useMutation } from "react-query";
import { t } from "@/i18n-js/instance";
import { paywallApi } from "@/react/api/paywallApi";
import type {
  PaywallFormRequest,
  PaywallResponse,
} from "@/react/api/paywallApi";
import { ModalManager } from "@/react/components/Layout/ModalManagerProvider";
import { PaywallMomentumModal } from "@/react/components/Paywalls/Admin/PaywallsAdmin/PaywallMomentumModal";
import type { Paywall } from "@/react/types";
import { isInactive } from "@circle-react/helpers/paywallHelpers";
import { PAYWALL_STATUS } from "@circle-react/types/Paywall";
import { useToast } from "@circle-react-uikit/ToastV2";
import { useInvalidatePaywallQueries } from "./useInvalidatePaywallQueries";

export interface UsePaywallApiProps {
  createCallback: (input: PaywallResponse) => void;
  paywall?: Paywall;
  updateCallback: (input: PaywallResponse) => void;
}

export const usePaywallApi = ({
  createCallback,
  updateCallback,
  paywall,
}: UsePaywallApiProps) => {
  const { success, error } = useToast();
  const { invalidatePaywallQueries } = useInvalidatePaywallQueries();
  const defaultErrorMessage = t("settings.paywalls.alerts.error.action_failed");

  const onSuccess = (
    response: PaywallResponse,
    message: string,
    callback: UsePaywallApiProps["createCallback"],
    showToast = true,
  ) => {
    if (showToast) {
      success(message);
    }
    if (callback) {
      callback(response);
    }
    // Invalidate all paywall queries to ensure counts and lists are updated
    invalidatePaywallQueries();
  };

  const onError = (errorObject: any) => {
    const messageError =
      typeof errorObject?.errorDetails === "string"
        ? errorObject?.errorDetails
        : defaultErrorMessage;
    error(messageError, {
      duration: "long",
    });
  };

  const publishOrArchiveParams = (
    publish: boolean,
  ): Partial<PaywallFormRequest> => ({
    publish: publish,
    paywall: {
      status: publish ? paywall?.status : PAYWALL_STATUS.INACTIVE,
    },
  });

  const updateMutation = useMutation<
    PaywallResponse,
    unknown,
    Partial<PaywallFormRequest>
  >(
    (formData: Partial<PaywallFormRequest>) =>
      paywallApi.updatePaywall(paywall?.id, formData),
    {
      onSuccess: response =>
        onSuccess(
          response,
          t("settings.paywalls.alerts.success.updated"),
          updateCallback,
        ),
      onError: onError,
    },
  );

  const createMutation = useMutation<
    PaywallResponse,
    unknown,
    PaywallFormRequest
  >((formData: PaywallFormRequest) => paywallApi.createPaywall(formData), {
    onSuccess: (response, formData: PaywallFormRequest) => {
      onSuccess(
        response,
        t("settings.paywalls.alerts.success.created"),
        createCallback,
        !formData.publish,
      );

      if (formData.publish) {
        void ModalManager.show(PaywallMomentumModal, {
          paywall: response,
        });
      }
    },
    onError: onError,
  });

  const publishMutation = useMutation<PaywallResponse>(
    () => paywallApi.updatePaywall(paywall?.id, publishOrArchiveParams(true)),
    {
      onSuccess: response => {
        onSuccess(
          response,
          t("settings.paywalls.alerts.success.published"),
          updateCallback,
          false,
        );
        if (!isInactive(paywall)) {
          void ModalManager.show(PaywallMomentumModal, {
            paywall: response,
          });
        }
      },
      onError: onError,
    },
  );

  const archiveMutation = useMutation<PaywallResponse>(
    () => paywallApi.updatePaywall(paywall?.id, publishOrArchiveParams(false)),
    {
      onSuccess: (response: PaywallResponse) =>
        onSuccess(
          response,
          t("settings.paywalls.alerts.success.archived"),
          updateCallback,
        ),
      onError: onError,
    },
  );

  const deleteMutation = useMutation<PaywallResponse>(
    () => paywallApi.deletePaywall(paywall?.id),
    {
      onSuccess: (response: PaywallResponse) =>
        onSuccess(
          response,
          t("settings.paywalls.alerts.success.deleted"),
          updateCallback,
        ),
      onError: onError,
    },
  );

  return {
    updateMutation,
    createMutation,
    publishMutation,
    archiveMutation,
    deleteMutation,
  };
};
